import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UserKeys, getGroups, getUserList } from '../api/users-api';
import { useMemo, useState } from 'react';
import useDebounce from '../utils/useDebounce';
import { CalendarIcon } from '@mui/x-date-pickers';
import { GroupHeader, GroupItems } from './autocomplete-groups-ui';
import AvatarChip from './avatar-chip';
import { emailRegex } from '../utils/regular-expressions';
import UserAvatar from './user-avatar';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import Check from '@mui/icons-material/Check';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import Group from '@mui/icons-material/Group';
import type { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Search from '@mui/icons-material/Search';

interface SharedEmailAutocompleteProps {
  sources: ('users' | 'groups')[];
  multiAutoCompleteValue?: string[];
  setMultiAutoCompleteValue?: (value: string[]) => void;
  setClearWasClicked?: (value: boolean) => void;
  disabled?: boolean;
  freeSolo?: boolean;
  newKey?: string;
  errors?: boolean;
  helperText?: string;
  required?: boolean;
  createdEmail?: string;
  sendType: string;
  includeCreatedUser?: boolean;
  renderChipsBelow?: boolean;
  /** Shows a search icon at the end of the input, in place of the clear (x) button. */
  showSearchIcon?: boolean;
}

export default function SharedEmailAutocomplete({
  disabled,
  freeSolo,
  multiAutoCompleteValue,
  setMultiAutoCompleteValue,
  setClearWasClicked,
  newKey,
  sources,
  errors,
  helperText,
  required,
  createdEmail,
  sendType,
  includeCreatedUser,
  renderChipsBelow = false,
  showSearchIcon = false,
}: SharedEmailAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 500);

  const handleDelete = (emailToDelete: string) => {
    const updatedValues =
      multiAutoCompleteValue?.filter((email) => email !== emailToDelete) || [];
    setMultiAutoCompleteValue?.(updatedValues);
  };

  const { data: userData, isFetching: userLoading } = useQuery({
    queryKey: [UserKeys.UserList, { debouncedInput }],
    queryFn: () =>
      getUserList(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput,
        '',
        null,
        null
      ),
    enabled: sources.includes('users') && debouncedInput !== '',
    placeholderData: keepPreviousData,
  });

  const { data: groupData, isFetching: groupsLoading } = useQuery({
    queryKey: [UserKeys.Groups, { debouncedInput }],
    queryFn: () =>
      getGroups(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput
      ),
    enabled: sources.includes('groups') && debouncedInput !== '',
    placeholderData: keepPreviousData,
  });

  interface Option {
    email: string;
    type: string;
    firstName?: string;
    lastName?: string;
    suspended?: boolean;
    archived?: boolean;
    numberOfMembers?: number;
  }

  const userOptions: Option[] = sources.includes('users')
    ? userData?.results.map((result) => {
        return {
          email: result.userEmail,
          type: 'user',
          firstName: result.firstName,
          lastName: result.lastName,
          suspended: result.suspended,
          archived: result.archived,
        };
      }) || []
    : [];
  const groupOptions: Option[] = sources.includes('groups')
    ? groupData?.results.map((result) => {
        return {
          email: result.email,
          type: 'group',
          numberOfMembers: result.directMemberCount,
        };
      }) || []
    : [];

  const options = [...userOptions, ...groupOptions];

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  const SmallAvatar = styled(Avatar)(() => ({
    width: 16,
    height: 16,
    backgroundColor: 'white',
  }));

  const handleOnChange = (
    newValue: (string | Option)[],
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'clear') {
      setClearWasClicked?.(true);

      if (includeCreatedUser) {
        setMultiAutoCompleteValue?.([createdEmail!]);
      } else {
        setMultiAutoCompleteValue?.([]);
      }
      return;
    }

    if (newValue.length > 0) {
      const validEmails = newValue
        .filter((val: any) => emailRegex.test(val.email || val))
        .map((val: any) => (typeof val === 'string' ? val : val.email));

      if (includeCreatedUser && !validEmails.includes(createdEmail)) {
        validEmails.push(createdEmail);
      }

      setMultiAutoCompleteValue?.(validEmails);
    } else {
      setMultiAutoCompleteValue?.([]);
    }
  };

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={1}
      sx={{ width: '100%' }}
    >
      <Box sx={{ width: '100%' }}>
        <Autocomplete
          id="shared-autocomplete"
          key={key}
          disabled={disabled}
          filterOptions={(x) => x}
          options={options}
          size={'small'}
          multiple
          freeSolo={freeSolo}
          disableClearable={showSearchIcon}
          limitTags={2}
          autoComplete
          autoHighlight
          includeInputInList
          filterSelectedOptions
          noOptionsText={
            debouncedInput ? 'No results found.' : 'Start typing to search...'
          }
          sx={{
            width: renderChipsBelow ? '50%' : '100%',
          }}
          value={
            multiAutoCompleteValue
              ?.map(
                (email) =>
                  options.find((opt) => opt.email === email) || {
                    email,
                    type: 'user',
                  }
              )
              .filter((option) => option.email !== createdEmail) || []
          }
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return (
                userData?.results.find((user) => user.userEmail === option)
                  ?.userEmail || option
              );
            } else {
              return option.email;
            }
          }}
          isOptionEqualToValue={(option, value) => {
            if (typeof value === 'string') {
              return option.email === value;
            }
            return option.email === value?.email;
          }}
          onChange={(_event, newValue, reason) => {
            handleOnChange(newValue, reason);
          }}
          renderOption={(props, option: Option) => {
            return (
              <ListItem {...props} dense>
                {option.type === 'user' ? (
                  <>
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        badgeContent={
                          <SmallAvatar
                            alt={`${option.firstName} ${option.lastName}`}
                            sx={{
                              border: (theme) =>
                                `1px solid ${
                                  option.suspended
                                    ? theme.palette.warning.main
                                    : option.archived
                                    ? theme.palette.info.main
                                    : theme.palette.success.main
                                }`,
                            }}
                          >
                            {option.suspended ? (
                              <DoNotDisturb
                                color="warning"
                                sx={{ width: 10 }}
                              />
                            ) : option.archived ? (
                              <ArchiveRounded color="info" sx={{ width: 10 }} />
                            ) : (
                              <Check color="success" sx={{ width: 10 }} />
                            )}
                          </SmallAvatar>
                        }
                        sx={{ mr: 2 }}
                      >
                        <UserAvatar userEmail={option.email} />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      id={option.email}
                      primary={option.email}
                      secondary={`${option.firstName} ${option.lastName}`}
                    />
                  </>
                ) : option.type === 'group' ? (
                  <>
                    <ListItemAvatar>
                      <Avatar>
                        <Group />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      id={option.email}
                      primary={option.email}
                      secondary={`${option.numberOfMembers} members`}
                      primaryTypographyProps={{ whiteSpace: 'normal' }}
                    />
                  </>
                ) : (
                  <>
                    <ListItemAvatar>
                      <Avatar>
                        <CalendarIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      id={option.email}
                      primary={option.email}
                      secondary={option.firstName}
                      primaryTypographyProps={{ whiteSpace: 'normal' }}
                    />
                  </>
                )}
              </ListItem>
            );
          }}
          renderTags={
            renderChipsBelow
              ? () => null
              : (selected) => {
                  return selected
                    .map((option) => {
                      if (option.email === createdEmail) {
                        return null;
                      }
                      return (
                        <AvatarChip
                          key={option.email}
                          label={option.email}
                          type={option.type === 'group' ? 'Group' : 'User'}
                          email={option.email}
                          onDelete={() => handleDelete(option.email)}
                        />
                      );
                    })
                    .filter(Boolean);
                }
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={sendType}
              required={required}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(userLoading || groupsLoading) && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                    {showSearchIcon && (
                      <Search
                        color="action"
                        data-testid="email-autocomplete-search-icon"
                      />
                    )}
                  </>
                ),
              }}
              error={errors}
              helperText={
                errors
                  ? helperText
                  : multiAutoCompleteValue && multiAutoCompleteValue.length > 0
                  ? ''
                  : helperText
              }
            />
          )}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) =>
            setInputValue(newInputValue)
          }
          groupBy={(option) => option.type}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>{params.group}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
        />
        {renderChipsBelow && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              width: '100%',
            }}
          >
            {multiAutoCompleteValue
              ?.filter((email) => email !== createdEmail)
              .map((email) => {
                const option = options.find((opt) => opt.email === email) || {
                  email,
                  type: sources.includes('groups') ? 'group' : 'user',
                };
                return (
                  <AvatarChip
                    key={email}
                    label={email}
                    type={option.type === 'group' ? 'Group' : 'User'}
                    email={email}
                    onDelete={() => handleDelete(email)}
                  />
                );
              })}
          </Box>
        )}
      </Box>
    </Stack>
  );
}
